/**
 * AdventureFileCard Component
 * Displays a file in a grid view with thumbnail, metadata, and actions
 */

import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Skeleton,
  Chip,
} from '@mui/material'
import {
  MoreVert,
  Image as ImageIcon,
  VideoFile,
  AudioFile,
  Description,
  InsertDriveFile,
} from '@mui/icons-material'
import type { AdventureMediaFileDto } from '@api/types/adventureFiles'
import { formatFileSize, getFileTypeIcon } from '@api/types/adventureFiles'

interface AdventureFileCardProps {
  file: AdventureMediaFileDto
  onClick?: () => void
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void
  isLoading?: boolean
}

function FileTypeIcon({ mimeType }: { mimeType: string }) {
  const type = getFileTypeIcon(mimeType)
  const iconProps = { sx: { fontSize: 48, color: 'text.secondary' } }

  switch (type) {
    case 'image':
      return <ImageIcon {...iconProps} />
    case 'video':
      return <VideoFile {...iconProps} />
    case 'audio':
      return <AudioFile {...iconProps} />
    case 'document':
      return <Description {...iconProps} />
    default:
      return <InsertDriveFile {...iconProps} />
  }
}

export function AdventureFileCard({
  file,
  onClick,
  onMenuClick,
  isLoading,
}: AdventureFileCardProps) {
  if (isLoading) {
    return <AdventureFileCardSkeleton />
  }

  const isImage = file.mimeType.startsWith('image/')
  const displayName = file.displayName || file.fileName

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick
          ? {
              borderColor: 'primary.main',
              boxShadow: 2,
            }
          : {},
      }}
      onClick={onClick}
    >
      {/* Thumbnail or icon */}
      <Box
        sx={{
          height: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          position: 'relative',
        }}
      >
        {isImage && file.thumbnailUrl ? (
          <CardMedia
            component="img"
            height="160"
            image={file.thumbnailUrl}
            alt={displayName}
            sx={{ objectFit: 'cover' }}
          />
        ) : isImage && file.fileUrl ? (
          <CardMedia
            component="img"
            height="160"
            image={file.fileUrl}
            alt={displayName}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <FileTypeIcon mimeType={file.mimeType} />
        )}

        {/* Usage count badge */}
        {file.usageCount > 0 && (
          <Chip
            label={`${file.usageCount} use${file.usageCount !== 1 ? 's' : ''}`}
            size="small"
            color="primary"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
            }}
          />
        )}

        {/* Dimensions badge for images */}
        {isImage && file.width && file.height && (
          <Chip
            label={`${file.width}x${file.height}`}
            size="small"
            variant="outlined"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(255,255,255,0.9)',
              fontSize: '0.7rem',
            }}
          />
        )}
      </Box>

      {/* File info */}
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Tooltip title={displayName}>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ fontWeight: 600 }}
          >
            {displayName}
          </Typography>
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          {formatFileSize(file.fileSize)}
        </Typography>
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {new Date(file.createdDate).toLocaleDateString()}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onMenuClick?.(e)
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  )
}

export function AdventureFileCardSkeleton() {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Skeleton variant="rectangular" height={160} />
      <CardContent sx={{ flexGrow: 1 }}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="40%" />
      </CardContent>
    </Card>
  )
}
